import { Button } from "$components/button/button";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import { useUser } from "$global-providers/user-provider";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { canPerformAction } from "$routes/patient/[:patientId]/_lib/invoices-v2/_lib/can-perform-action";
import { TakePaymentModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/take-payment-modal-v2";
import { useFetchPatientInsuranceRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-fetch-patient-insurance-rxdb";
import { PatientType, PayerType } from "elephant-web/gql";
import { useMemo, useState } from "react";
import type { RxDocument } from "rxdb";
import { invoiceRecordFromDocument } from "../invoice-record-from-document";
import { InpatientDetails } from "./_lib/inpatient-details";
import { InvoiceCardActions } from "./_lib/invoice-card-actions";
import { InvoiceLineItemCard } from "./_lib/invoice-line-item-card";
import { InvoiceLineItems, stacksLineItems } from "./_lib/invoice-line-items";
import { InvoiceStatusChip } from "./_lib/invoice-status-chip";
import { InvoiceTotals } from "./_lib/invoice-totals";
import { buildInvoiceView } from "./_lib/invoice-view";
import { PayersOnInvoice } from "./_lib/payers-on-invoice";
import { PaymentHistory } from "./_lib/payment-history";
import { useResolveCashierName } from "./_lib/use-cashier-names";
import {
  formatInvoiceDate,
  useInvoiceCardData,
} from "./_lib/use-invoice-card-data";
import { InvoiceCardHeader } from "./invoice-card-header";
import { InvoiceCardSection, useInvoiceCardLayout } from "./invoice-card-shell";

export const FinalisedInvoiceCard: FC<{
  doc: RxDocument<RxInvoice>;
  patientId: string;
  priceList: PriceList;
  dateLabel: string;
  clinicianName?: string;
  onToggleExpanded: () => void;
}> = ({
  doc,
  patientId,
  priceList,
  dateLabel,
  clinicianName,
  onToggleExpanded,
}) => {
  const user = useUser();
  const { width, isCompact } = useInvoiceCardLayout();
  const isStacked = stacksLineItems("finalised", width);
  const cardData = useInvoiceCardData(priceList, doc.balance.currency);
  const { formatMoney } = cardData;
  const patientInsurance = useFetchPatientInsuranceRxdb(patientId);
  const resolveCashier = useResolveCashierName(doc.transactions);

  const [takingPayment, setTakingPayment] = useState(false);
  const calculatedInvoice = Invoice.fromRecord(
    invoiceRecordFromDocument(doc),
  ).toRecord();

  const membershipByInsurerName = useMemo(
    () =>
      new Map(
        patientInsurance?.insuranceType
          ? [
              [
                patientInsurance.insuranceType,
                patientInsurance.insuranceDetails?.insuranceNumber ?? "",
              ],
            ]
          : [],
      ),
    [patientInsurance],
  );

  const view = useMemo(
    () =>
      buildInvoiceView({
        toMajor: cardData.toMajor,
        formatMoney: cardData.formatMoney,
        departmentNameById: cardData.departmentNameById,
        formatItemDate: cardData.formatItemDate,
        formatTimestamp: cardData.formatTimestamp,
        resolveCashier,
        invoice: calculatedInvoice,
        priceList,
        membershipByInsurerName,
        fallbackItemDate: doc.meta.createdAt,
      }),
    [
      calculatedInvoice,
      cardData,
      doc.meta.createdAt,
      membershipByInsurerName,
      priceList,
      resolveCashier,
    ],
  );

  const settled = view.outstanding === 0;
  const invoiceStatus = calculatedInvoice.invoiceStatus;

  const headlineNote = settled
    ? isCompact
      ? "paid in full"
      : `paid in full · ${formatMoney(view.itemsBilled)} billed`
    : view.status === "partial"
      ? isCompact
        ? `${formatMoney(view.paidToDate)} of ${formatMoney(view.patientBalance)} paid`
        : `due from patient · ${formatMoney(view.paidToDate)} of ${formatMoney(view.patientBalance)} paid`
      : isCompact
        ? "due from patient"
        : `due from patient · ${formatMoney(view.itemsBilled)} billed`;

  const lastSettledAt = view.payments
    .filter((payment) => payment.kind === "patient-payment")
    .map((payment) => payment.recordedAt)
    .sort((a, b) => a.getTime() - b.getTime())
    .at(-1);

  const canTakePayment = canPerformAction(
    user.role,
    "takePayment",
    invoiceStatus,
  );
  const showHistory = canPerformAction(
    user.role,
    "paymentHistory",
    invoiceStatus,
  );

  const rows = view.lines.map((line) => line.row);

  return (
    <>
      <InvoiceCardHeader
        dateLabel={dateLabel}
        encounterTypeLabel={String(doc.patientType)}
        clinicianName={clinicianName}
        amount={formatMoney(view.outstanding)}
        note={headlineNote}
        chip={
          <InvoiceStatusChip status={invoiceStatus} moneyStatus={view.status} />
        }
        onCollapse={onToggleExpanded}
        collapseLabel="Collapse invoice"
      />

      {doc.patientType === PatientType.Inpatient && (
        <InpatientDetails
          mode="read-only"
          values={{
            ward: doc.inpatientDetails?.ward ?? "",
            patientNumber: doc.inpatientDetails?.inpatientNumber ?? "",
            admissionDate: doc.inpatientDetails?.admission
              ? formatInvoiceDate(new Date(doc.inpatientDetails.admission))
              : "",
            dischargeDate: doc.inpatientDetails?.discharge
              ? formatInvoiceDate(new Date(doc.inpatientDetails.discharge))
              : "",
          }}
        />
      )}

      {isStacked ? (
        <InvoiceCardSection
          paddingTop={0}
          paddingBottom={0}
          disablePadX
          data-testid="invoicing.v2.card.lineItems"
        >
          {rows.map((row) => (
            <InvoiceLineItemCard key={row.id} variant="finalised" row={row} />
          ))}
        </InvoiceCardSection>
      ) : (
        <InvoiceLineItems
          variant="finalised"
          rows={rows}
          data-testid="invoicing.v2.card.lineItems"
        />
      )}

      <PayersOnInvoice
        payers={view.payers}
        formatMoney={formatMoney}
        mode="read-only"
      />

      <InvoiceTotals
        mode="finalised"
        itemsBilled={view.itemsBilled}
        coverage={view.coverage}
        patientBalance={view.patientBalance}
        paidToDate={view.paidToDate}
        outstanding={view.outstanding}
        formatMoney={formatMoney}
      />

      {showHistory && (
        <PaymentHistory payments={view.payments} formatMoney={formatMoney} />
      )}

      {takingPayment && (
        <TakePaymentModalV2
          doc={doc}
          open
          onClose={() => setTakingPayment(false)}
        />
      )}

      <InvoiceCardActions
        doc={doc}
        patientId={patientId}
        invoiceStatus={invoiceStatus}
        mode="finalised"
        priceList={priceList}
        payerNames={view.insurers.map((insurer) => insurer.name)}
        totalRefundableInPence={
          calculatedInvoice.payers.find(
            (payer) => payer.type === PayerType.Patient,
          )?.balance.paid ?? 0
        }
        currency={String(doc.balance.currency)}
        settledNote={
          settled && lastSettledAt
            ? `Paid in full · ${formatInvoiceDate(lastSettledAt)}`
            : undefined
        }
        primaryAction={
          canTakePayment &&
          !settled && (
            <Button color="primary" onClick={() => setTakingPayment(true)}>
              {view.status === "partial"
                ? "Take remaining payment"
                : "Take payment"}
            </Button>
          )
        }
      />
    </>
  );
};
